import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Bridge your CQT\npageTitle: Bridge your CQT | Covalent Network | Covalent\n---\n\n{% breadcrumb title=\"Bridge your CQT\" /%}\n\n# {% $markdoc.frontmatter.title %}\n\nCovalent has partnered with Wormhole to enable the secure bridging of CQT to other networks.\n\nWormhole is a generic message-passing protocol that connects to multiple chains. Wormhole does this by emitting messages from one chain which are observed by a Guardian network of nodes and verified. After verification, this message is submitted to the target chain for processing.\n\nFor more information on Wormhole, please visit its [documentation.](https://docs.wormhole.com/wormhole/)\n\n## How to Bridge CQT using Wormhole\n\nBirding through Wormhole is intuitive and easy. Outlined below are the steps involved to bridge CQT to Moonbeam using MetaMask.\n\nFirst, go to [https://www.portalbridge.com/#/transfer](https://www.portalbridge.com/#/transfer) and connect your wallet.\n\n{% diagram slug=\"bridge-guide-1\" /%}\n\nFrom here, you need select your source and target chain:\n\n- **Source**: Ethereum\n- **Target**: Moonbeam\n\nNext, CQT needs to be selected under \"Select a Token\". One can search for CQT and it should appear in the search results. Alternatively, you can search for CQT using its Ethereum contract address: 0xD417144312DbF50465b1C641d016962017Ef6240\n\n{% diagram slug=\"bridge-guide-2\" /%}\n\nOnce you have selected CQT, you should see something similar to the window below. You can enter how much you wish to bridge at this point.\n\n{% diagram slug=\"bridge-guide-3\" /%}\n\nAfter selecting \"Next\", you will be shown the below prompt. Select the target chain gas fee payment method, GLMR in this case. The manual payment method was also used.\n\n{% diagram slug=\"bridge-guide-4\" /%}\n\nSelect \"Next\". You will now see the option to approve the contract interaction. This will incur a gas fee. In this case, only the designated amount was approved.\n\n{% diagram slug=\"bridge-guide-5\" /%}\n\nOnce approved, select \"Transfer”.\n\n{% diagram slug=\"bridge-guide-6\" /%}\n\nIn the Pop-up \"Are you sure?\" click \"Confirm\".\n\n{% diagram slug=\"bridge-guide-7\" /%}\n\nHaving waiting for confirmations on the blockchain, one can then redeem their WholeCQT by selecting \"Redeem\". In the pop-up click \"Confirm/Approve\" to redeem the tokens.\n\n{% diagram slug=\"bridge-guide-8\" /%}\n\nVoila! You now have WholeCQT on Moonbeam.\n\n{% diagram slug=\"bridge-guide-9\" /%}\n";
const filepath = "/docs/covalent-network/bridge.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
